import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { auth, db, ref, get, set, onValue } from "../Firebase/firebase-config";
import { onAuthStateChanged } from "firebase/auth";

const AccountContext = createContext();

export const AccountProvider = ({ children }) => {
  const [currentAccount, setCurrentAccount] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [accountSwitchTrigger, setAccountSwitchTrigger] = useState(0);

  // Main listener for authenticated user
  useEffect(() => {
    const unsubscribeAuth = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        setCurrentAccount(null);
        setAccounts([]);
        setLoading(false);
        return;
      }

      // Listen to main user's profile
      const mainRef = ref(db, `profileDashboard/${user.uid}`);

      const unsubscribeMain = onValue(mainRef, async (mainSnap) => {
        if (!mainSnap.exists()) {
          setLoading(false);
          return;
        }

        const mainData = { uid: user.uid, ...mainSnap.val() };
        let allAccounts = [];

        // Add main account
        const mainAccount = {
          uid: mainData.uid,
          name: mainData.company_name || "My Account",
          type: mainData.type || "main",
          mainUid: mainData.uid,
          ...mainData,
        };

        allAccounts.push(mainAccount);

        // If main user, fetch all associated firms
        if (mainData.type === "main") {
          const firmsRef = ref(db, `profileDashboard/${user.uid}/firms`);
          const firmsSnap = await get(firmsRef);   // One-time fetch is better here

          if (firmsSnap.exists()) {
            const firmUids = Object.keys(firmsSnap.val());

            // Fetch all firms in parallel (much better)
            const firmPromises = firmUids.map(async (firmUid) => {
              const firmSnap = await get(ref(db, `profileDashboard/${firmUid}`));
              if (firmSnap.exists()) {
                const firmData = firmSnap.val();
                return {
                  uid: firmUid,
                  name: firmData.company_name || "Unnamed Firm",
                  type: "firm",
                  mainUid: mainData.uid,
                  parentUid: mainData.uid,
                  ...firmData,
                };
              }
              return null;
            });

            const firms = (await Promise.all(firmPromises)).filter(Boolean);
            allAccounts = [...allAccounts, ...firms];
          }
        }

        setAccounts(allAccounts);

        // Restore last selected account
        const selectedSnap = await get(ref(db, `profileDashboard/${user.uid}/currentAccountUid`));
        const selectedUid = selectedSnap.exists() ? selectedSnap.val() : user.uid;

        const selectedAccount = allAccounts.find(a => a.uid === selectedUid) || mainAccount;

        setCurrentAccount(selectedAccount);
        setLoading(false);
      });

      return () => unsubscribeMain();
    });

    return () => unsubscribeAuth();
  }, []);

  // Switch Account
  const switchAccount = useCallback(async (uid) => {
    const accountToSwitch = accounts.find(a => a.uid === uid);
    if (!accountToSwitch || !auth.currentUser) return;

    try {
      await set(ref(db, `profileDashboard/${auth.currentUser.uid}/currentAccountUid`), uid);
      setCurrentAccount(accountToSwitch);
      setAccountSwitchTrigger(prev => prev + 1);
    } catch (error) {
      console.error("Failed to switch account:", error);
    }
  }, [accounts]);

  // Reload all accounts (useful after adding new firm)
  const reload = useCallback(async () => {
    if (!auth.currentUser) return;
    // This will trigger the onValue listener again
    setLoading(true);
  }, []);

  const value = {
    currentAccount,
    accounts,
    switchAccount,
    loading,
    reload,
    accountSwitchTrigger,
    isMainUser: currentAccount?.type === "main",
  };

  return (
    <AccountContext.Provider value={value}>
      {children}
    </AccountContext.Provider>
  );
};

export const useAccount = () => useContext(AccountContext);